import { ConjointQuestion } from '@focaldata/cin-ui-components'
import { ConjointPanelResponse } from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Conjoint/ConjointItem.types'
import {
  CfmContext,
  CfmData
} from 'containers/QuestionFlowManager/QuestionFlowManager'
import useEntryProgress from 'hooks/useEntryProgress'
import useEntryRenderingDateTime from 'hooks/useEntryRenderingDateTime'
import { useMediaLoc } from 'hooks/useMediaLoc'
import { useQuestionnaireParams } from 'hooks/useQuestionnaireParams'
import { useQuestionTimer } from 'hooks/useQuestionTimer'
import useSendConjointResponse from 'hooks/useSendConjointResponse'
import { QuestionnaireEntry, ConjointItem } from 'model/questionnaire'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import displayEntityBasedOnLogic from 'utils/displayEntityBasedOnLogic'
import { setHiddenByDisplayLogic } from 'utils/hiddenByDisplayLogic'

const ConjointEntry = ({ entry }: { entry: QuestionnaireEntry }) => {
  const { formatMessage } = useIntl()
  const entryItem = entry.entryItem as ConjointItem
  const progress = useEntryProgress(entry)
  const timer = useQuestionTimer(entryItem)
  const { getNextEntryPosition, preview } = useContext<CfmData>(CfmContext)
  const mandatoryParams = useQuestionnaireParams()
  const mediaLoc = useMediaLoc()
  const { sendConjointResponse } = useSendConjointResponse()
  const renderingDateTime = useEntryRenderingDateTime(entry)

  const loc = {
    ...mediaLoc,
    next: formatMessage({
      id: 'question_next',
      defaultMessage: 'Next'
    }),
    instructions: formatMessage({
      id: 'question_conjoint_select_package',
      defaultMessage: 'Select your preferred option:'
    })
  }

  const handleOnNext = (panelResponses: ConjointPanelResponse[]) => {
    if (!preview) {
      sendConjointResponse({
        renderedEntryPosition: entry.renderedEntryPosition,
        questionId: entryItem.question.questionId,
        panelResponses,
        enteringDatetime: renderingDateTime
      })
    }

    getNextEntryPosition()
  }

  const shouldDisplayEntityBasedOnLogic = displayEntityBasedOnLogic(
    mandatoryParams.respondentId,
    mandatoryParams.surveyId,
    entryItem.questionLogic,
    entry.entryType
  )

  // Move to the next question if the current question should not be displayed due to display logic
  if (!shouldDisplayEntityBasedOnLogic) {
    setHiddenByDisplayLogic(
      mandatoryParams.respondentId,
      mandatoryParams.surveyId,
      entryItem.question.questionId
    )
    getNextEntryPosition()
  }

  return (
    <ConjointQuestion
      item={entryItem}
      loc={loc}
      next={{ onNext: handleOnNext, loading: false, disabled: false }}
      progress={progress}
      preview={preview}
      timer={timer}
    />
  )
}

export default ConjointEntry
